import Tooltip from "@/tooltip";
import { ArrowDownTrayIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { format, parse } from "date-fns";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { sumBy } from "lodash";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate } from "react-router-dom";
import { Actions as FSSREPORTACTIONS } from "../../store/actions/fssReportActions";
import EIDLoginModal from "./EIDLoginModal";
import Styles from "./EIDStyle.module.css";
import FS7ValidationModal from "./FS7ValidationModal";
import RegenerateFormsModal from "./RegenerateFormsModal";
import apiURL from "@/apiURL";
import ActionList from "@/components/common/custom-selectors/ActionList";
import { DocumentIcon, DocumentTextIcon, PencilSquareIcon, TableCellsIcon } from "@/icons/solid";

const round2 = (value) =>
	(value ? value : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const deductionColors = ["#EA611C", "#B68D40", "#B13EF0", "#122620", "#FAB300", "#D6AD60", "#22508A", "#FFD78C"];

const earningColors = ["#7F80EB", "#FF5786", "#DF362D", "#E57F84", "#2F5061", "#4297A0", "#B4F8C8", "#821D30"];
const Submissions = ({
	companyName,
	peNumber,
	currency,
	downloadFSSReport,
	openEIDModal,
	loggedIn,
	submissionForm,
	paymentsPerMonth,
	totalPaid,
	credentials,
	openFS7ValidationModal,
	validated,
	submitFS7,
	openRegenerateFormsModal,
	listFssReports,
	existingFS5s,
	totals,
}) => {
	const [expandBreakdown, setExpandBreakdown] = useState(false);
	const [expandMonthlyPaymentsBreakdown, setExpandMonthlyPaymentsBreakdown] = useState(false);

	const TooltipComponent = ({ fieldName, value, existing }) => {
		const difference = parseFloat(value) - parseFloat(existing);
		const roundedValue = round2(value);
		const roundedExisting = round2(existing);
		return (
			<div
				style={{
					gridColumn: "span 1 / span 6",
					textAlign: "right",
					color: roundedValue != roundedExisting ? "red" : "inherit",
				}}
			>
				<Tooltip
					arrow={false}
					content={
						<>
							<span>
								Paid {fieldName}: {currency}
								{roundedValue}
							</span>
							<br />
							<span>
								FS5 {fieldName}: {currency}
								{roundedExisting}
							</span>
							<br />
							<span>
								Difference {fieldName}: {currency}
								{round2(difference)}
							</span>
						</>
					}
					isOpen={value != existing ? undefined : false}
				>
					{currency}
					{roundedValue}
				</Tooltip>
			</div>
		);
	};
	const { year, form, summary, submitted } = { ...submissionForm };

	useEffect(() => {
		listFssReports({
			name: "fs5",
			year,
		});
	}, []);

	if (!submissionForm) return <Navigate to="/organisation/tax-forms" />;

	const ssc = summary?.SSC;
	const mfc = summary?.MFC;
	const fss = summary?.ftFSS + summary?.ptFSS + summary?.otFSS;
	const gross = summary?.ftGross + summary?.ptGross + summary?.otGross;
	const totalDue = ssc + mfc + fss;
	const underOver = totalPaid ? totalPaid - totalDue : 0;

	const grossEntries = [
		{
			name: "Gross Emoluments (FSS Main or FSS Other applies)",
			value: summary?.ftGross,
		},
		{
			name: "Overtime (Eligible for 15% tax deductions)",
			value: summary?.otGross,
		},
		{
			name: `Director's Fees`,
			value: summary?.directorFees,
		},
		{
			name: "Gross Emoluments (FSS Part-Time Method Applies)",
			value: summary?.ptGross,
		},
		{
			name: "Fringe Benefits - Exlcluding Share Options",
			value: summary?.assets + summary?.carFBV + summary?.other - summary?.nonTaxableCarAllowance,
		},
		{
			name: "Share Options Fringe Benefits Taxed at 15%",
			value: summary?.fringeShareOptions,
		},
	];

	const totalDueEntries = [
		{
			name: "Tax Deductions FSS Main or FSS Other Applies",
			value: summary?.ftFSS,
		},
		{
			name: "Tax Deductions (Eligible Overtime",
			value: summary?.otFSS,
		},
		{
			name: `Tax Deductions (FSS Part-Time Method Applies)`,
			value: summary?.ptFSS,
		},
		{
			name: "Tax Arrears Deductions (as per amount on PCU2(A))",
			value: summary?.taxArrears,
		},
		{
			name: "15% Tax On Share Options",
			value: summary?.taxShareOptions,
		},
	];

	const fs7Summary = [
		{
			label: "Number of Employees:",
			value: summary?.ptEmployees + summary?.ftEmployees || 0,
		},
		{
			label: "Total Paid:",
			value: `${currency}${(totalPaid || 0).toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})}`,
		},
		{
			label: "10% of Wage Supplement:",
			value: `${currency}${(summary?.covid19 || 0).toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})}`,
		},
		{
			label: "Total Due:",
			value: `${currency}${(totalDue || 0).toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})}`,
		},
		{
			label: "Underpaid/Overpaid:",
			value: `${currency}${(underOver || 0).toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})}`,
		},
	];

	const chartOptions = {
		chart: {
			type: "pie",
			backgroundColor: "transparent",
			height: 250,
			credits: false,
		},
		credits: false,
		title: {
			text: "",
		},
		tooltip: {
			valuePrefix: currency,
		},
		series: [
			{
				size: "95%",
				innerSize: "65%",
				dataLabels: {
					enabled: false,
				},
				data: [
					...totalDueEntries
						.filter((item) => !!item.value)
						.map((key, index) => ({
							color: deductionColors[index],
							name: key.name,
							y: key.value,
						})),
					// {
					//     color: netColor || netDefaultColor,
					//     name: "Net Salary",
					//     y: net != 0 ? net : null
					// }
				],
			},
			{
				size: "60.5%",
				dataLabels: {
					enabled: false,
				},
				data: grossEntries
					.filter((item) => !!item.value)
					.map((key, index) => ({
						color: earningColors[index],
						name: key.name,
						y: key.value,
					})),
			},
		],
	};

	const taxForms = [
		{
			icon: PencilSquareIcon,
			label: "Download FS3s",
			onClick: () => downloadFSSReport({ year, name: "fs3", employee: "all" }),
		},
		{
			icon: PencilSquareIcon,
			label: "Download FS7",
			onClick: () => downloadFSSReport({ year, name: form }),
		},
	];

	const lodgementFiles = [
		{
			icon: DocumentIcon,
			label: "Download F3 File",
			target: "_blank",
			download: true,
			href: `${apiURL}/fss/f3/${year}`,
		},
		{
			icon: TableCellsIcon,
			label: "Download F3 CSV",
			target: "_blank",
			download: true,
			href: `${apiURL}/fss/f3/csv/${year}`,
		},
		{
			icon: DocumentTextIcon,
			label: "Download SS File",
			target: "_blank",
			download: true,
			href: `${apiURL}/fss/ss/${year}`,
		},
		{
			icon: TableCellsIcon,
			label: "Download SS CSV",
			target: "_blank",
			download: true,
			href: `${apiURL}/fss/ss/csv/${year}`,
		},
	];

	return (
		<>
			<FS7ValidationModal year={year} peNumber={peNumber} />
			<EIDLoginModal peNumber={peNumber} year={year} />
			<RegenerateFormsModal />
			<div className="content bg-gray-lighter">
				<div className="col-xs-8">
					<div className="pull-left">
						<h1 className="page-heading">FSSXpress™ {form?.toUpperCase()}</h1>
						<h4>
							{companyName} - {year}
						</h4>
						<div>PE Number: {peNumber}</div>
					</div>
				</div>
				<div className="col-xs-4">
					<div
						className="btn btn-success pull-right"
						style={{ marginTop: 20 }}
						onClick={() => openRegenerateFormsModal({ status: true, year: year, reportType: form })}
					>
						Regenerate Forms
					</div>
				</div>
				<div className="clearfix"></div>
			</div>
			<div className="clearfix"></div>
			<div className="content">
				<div className="row">
					<div
						style={{
							display: "grid",
							gridGap: "1.5rem",
							grid: "1.5rem",
							gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
						}}
					>
						<div style={{ gridColumn: "span 3 / span 6" }}>
							<h4 style={{ textAlign: "center" }}>Net Emoluments</h4>
							<HighchartsReact
								highcharts={Highcharts}
								options={chartOptions}
							/>
							<h4 style={{ textAlign: "center" }}>
								{currency}
								{(gross - totalDue || 0).toLocaleString(undefined, {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})}{" "}
							</h4>
						</div>
						<div style={{ gridColumn: "span 3 / span 6" }}>
							<h4 style={{ textAlign: "center" }}>FS7 Summary</h4>
							<div
								className="form-section"
								style={{ paddingBottom: "1rem" }}
							>
								<div
									style={{
										marginTop: "1rem",
										marginBottom: "1.5rem",
										padding: "1rem",
										borderRadius: "1rem",
										marginLeft: "1rem",
										marginRight: "1rem",
										background: "white",
									}}
								>
									{fs7Summary?.map((item) => (
										<div
											style={{
												padding: "1rem",
												display: "grid",
												gridGap: "1.5rem",
												grid: "1.5rem",
												gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
											}}
										>
											<div style={{ gridColumn: "span 3 / span 6" }}>
												<h5>{item.label}</h5>
											</div>
											<div style={{ gridColumn: "span 3 / span 6", textAlign: "right" }}>
												<h5>{item.value}</h5>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div
					className="form-section"
					style={{ paddingBottom: "1rem" }}
				>
					<div className="col-xs-11">
						<h4>FS7 Breakdown</h4>
					</div>
					<div className="col-xs-1">
						<ChevronDownIcon
							style={{
								minWidth: "2.25rem",
								width: "2.25rem",
								marginLeft: 10,
								marginRight: 10,
								verticalAlign: "text-bottom",
								cursor: "pointer",
								display: "inline-block",
								textAlign: "center",
								transform: expandBreakdown ? "rotate(180deg)" : "rotate(0deg)",
								transition: "transform 0.2s ease-in-out",
							}}
							onClick={() => setExpandBreakdown(!expandBreakdown)}
						/>
					</div>
					<div className="clearfix"></div>
					{expandBreakdown ? (
						<>
							<div className="section-header">
								<h1>Gross Emoluments</h1>
							</div>
							<div
								style={{
									marginTop: "1rem",
									marginBottom: "1.5rem",
									padding: "1rem",
									borderRadius: "1rem",
									marginLeft: "1rem",
									marginRight: "1rem",
									background: "white",
								}}
							>
								{grossEntries?.map((item) => (
									<div
										style={{
											padding: "1rem",
											display: "grid",
											gridGap: "1.5rem",
											grid: "1.5rem",
											gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
										}}
									>
										<div style={{ gridColumn: "span 3 / span 6" }}>
											<h5>{item.name}</h5>
										</div>
										<div style={{ gridColumn: "span 3 / span 6", textAlign: "right" }}>
											<h5>
												{currency}
												{(item.value || 0).toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</h5>
										</div>
									</div>
								))}
							</div>
							<div className="section-header">
								<h1>Tax Deductions Due</h1>
							</div>
							<div
								style={{
									marginTop: "1rem",
									marginBottom: "1.5rem",
									padding: "1rem",
									borderRadius: "1rem",
									marginLeft: "1rem",
									marginRight: "1rem",
									background: "white",
								}}
							>
								{totalDueEntries?.map((item) => (
									<div
										style={{
											padding: "1rem",
											display: "grid",
											gridGap: "1.5rem",
											grid: "1.5rem",
											gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
										}}
									>
										<div style={{ gridColumn: "span 3 / span 6" }}>
											<h5>{item.name}</h5>
										</div>
										<div style={{ gridColumn: "span 3 / span 6", textAlign: "right" }}>
											<h5>
												{currency}
												{(item.value || 0).toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</h5>
										</div>
									</div>
								))}
							</div>
							<div className="section-header">
								<h1>FS7 Summary</h1>
							</div>
							<div
								style={{
									marginTop: "1rem",
									marginBottom: "1.5rem",
									padding: "1rem",
									borderRadius: "1rem",
									marginLeft: "1rem",
									marginRight: "1rem",
									background: "white",
								}}
							>
								{fs7Summary?.map((item) => (
									<div
										style={{
											padding: "1rem",
											display: "grid",
											gridGap: "1.5rem",
											grid: "1.5rem",
											gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
										}}
									>
										<div style={{ gridColumn: "span 3 / span 6" }}>
											<h5>{item.label}</h5>
										</div>
										<div style={{ gridColumn: "span 3 / span 6", textAlign: "right" }}>
											<h5>{item.value}</h5>
										</div>
									</div>
								))}
							</div>
						</>
					) : null}
				</div>

				{paymentsPerMonth?.length > 0 ? (
					<div
						className="form-section"
						style={{ paddingBottom: "1rem" }}
					>
						<div className="col-xs-11">
							<h4>Monthly Payments Breakdown</h4>
						</div>
						<div className="col-xs-1">
							<ChevronDownIcon
								style={{
									minWidth: "2.25rem",
									width: "2.25rem",
									marginLeft: 10,
									marginRight: 10,
									verticalAlign: "text-bottom",
									cursor: "pointer",
									display: "inline-block",
									textAlign: "center",
									transform: expandMonthlyPaymentsBreakdown ? "rotate(180deg)" : "rotate(0deg)",
									transition: "transform 0.2s ease-in-out",
								}}
								onClick={() => setExpandMonthlyPaymentsBreakdown(!expandMonthlyPaymentsBreakdown)}
							/>
						</div>
						<div className="clearfix"></div>
						{expandMonthlyPaymentsBreakdown ? (
							<div
								style={{
									marginTop: "1rem",
									marginBottom: "1.5rem",
									padding: "1rem",
									borderRadius: "1rem",
									marginLeft: "1rem",
									marginRight: "1rem",
									background: "white",
								}}
							>
								<div
									style={{
										padding: "1rem",
										display: "grid",
										gridGap: "1.5rem",
										grid: "1.5rem",
										gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
									}}
								>
									<div style={{ gridColumn: "span 1 / span 6" }}>
										<h5>Month</h5>
									</div>
									<div style={{ gridColumn: "span 1 / span 6" }}>
										<h5>Payment Date</h5>
									</div>
									<div style={{ gridColumn: "span 1 / span 6" }}>
										<h5>Reference</h5>
									</div>
									<div style={{ gridColumn: "span 1 / span 6", textAlign: "right" }}>
										<h5>FSS Tax</h5>
									</div>
									<div style={{ gridColumn: "span 1 / span 6", textAlign: "right" }}>
										<h5>SSC</h5>
									</div>
									<div style={{ gridColumn: "span 1 / span 6", textAlign: "right" }}>
										<h5>Maternity Fund</h5>
									</div>
									<div style={{ gridColumn: "span 1 / span 6", textAlign: "right" }}>
										<h5>Total</h5>
									</div>
									{paymentsPerMonth?.map((item) => {
										const existingMonth = { ...existingFS5s?.[item.month] };
										const sscToAdd = parseFloat(item.ssc) || 0;
										const existingSSC = existingMonth.SSC || 0;
										const mfcToAdd = parseFloat(item.mfc) || 0;
										const existingMFC = existingMonth.MFC || 0;
										const fssToAdd = parseFloat(item.fss) || 0;
										const existingFSS =
											existingMonth.ptFSS + existingMonth.ftFSS + existingMonth.otFSS || 0;

										const total = sscToAdd + mfcToAdd + fssToAdd;
										const existingTotal = existingSSC + existingFSS + existingMFC;
										return (
											<>
												<div style={{ gridColumn: "span 1 / span 6" }}>
													<h5>{item.month}</h5>
												</div>
												<div style={{ gridColumn: "span 1 / span 6" }}>
													{format(
														parse(item.paymentDate, "yyyy-MM-dd", new Date()),
														"do MMM yyyy",
													)}
												</div>
												<div style={{ gridColumn: "span 1 / span 6" }}>{item.reference}</div>
												<TooltipComponent
													fieldName={"FSS"}
													value={fssToAdd}
													existing={existingFSS}
												/>
												<TooltipComponent
													fieldName={"SSC"}
													value={sscToAdd}
													existing={existingSSC}
												/>
												<TooltipComponent
													fieldName={"MFC"}
													value={mfcToAdd}
													existing={existingMFC}
												/>
												<TooltipComponent
													fieldName={"Total"}
													value={total}
													existing={existingTotal}
												/>
											</>
										);
									})}
									<div style={{ gridColumn: "span 3 / span 6" }}>
										<h5>Totals</h5>
									</div>
									<TooltipComponent
										fieldName={"FSS"}
										value={totals.paid.FSS}
										existing={totals.fs5.FSS}
									/>
									<TooltipComponent
										fieldName={"SSC"}
										value={totals.paid.SSC}
										existing={totals.fs5.SSC}
									/>
									<TooltipComponent
										fieldName={"MFC"}
										value={totals.paid.MFC}
										existing={totals.fs5.MFC}
									/>
									<TooltipComponent
										fieldName={"Total"}
										value={totals.paid.total}
										existing={totals.fs5.total}
									/>
								</div>
							</div>
						) : null}
					</div>
				) : null}

				{!submitted ? (
					<div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", paddingTop: 0 }}>
						<div style={{ display: "flex", justifyContent: "center", gridColumnStart: 2 }}>
							<Tooltip
								as="button"
								className="btn btn-default"
								onClick={() => openFS7ValidationModal(year, credentials)}
								disabled={!loggedIn}
								style={{ marginRight: 5 }}
								content={"Login to Validate FS7"}
								isOpen={loggedIn ? false : undefined}
							>
								Validate FS7
							</Tooltip>
						</div>
						<div style={{ display: "flex", justifyContent: "center", gridColumnStart: 3 }}>
							{!loggedIn ? (
								<button
									className={`btn ${Styles["btn-eid"]}`}
									onClick={() => openEIDModal()}
									style={{ marginRight: 5 }}
								>
									Login with E-ID
								</button>
							) : null}
						</div>
						<div style={{ display: "flex", justifyContent: "center", gridColumnStart: 4 }}>
							<Tooltip
								as="button"
								className="btn btn-success"
								onClick={() => submitFS7(year, credentials)}
								style={{ marginRight: 5 }}
								disabled={!loggedIn || !validated}
								content={!validated ? "Validate to Submit FS7" : "Login to Submit FS7"}
								isOpen={loggedIn && validated ? false : undefined}
							>
								Submit Using FSSXpress™
							</Tooltip>
						</div>
					</div>
				) : null}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(6, 1fr)",
						paddingTop: "2rem",
					}}
				>
					<div style={{ display: "flex", justifyContent: "center", gridColumnStart: 3 }}>
						<ActionList
							className="btn btn-default"
							type="button"
							as="button"
							direction="up"
							forceDirection={true}
							options={lodgementFiles}
							style={{ marginRight: 5 }}
							distance={10}
						>
							<ArrowDownTrayIcon
								className="h-5 w-5 text-dark-gray"
								style={{ verticalAlign: "middle", marginRight: 2 }}
							/>
							Lodgement Files
						</ActionList>
					</div>
					<div style={{ display: "flex", justifyContent: "center", gridColumnStart: 4 }}>
						<ActionList
							className="btn btn-default"
							type="button"
							as="button"
							direction="up"
							options={taxForms}
							forceDirection={true}
							distance={10}
						>
							<ArrowDownTrayIcon
								className="h-5 w-5 text-dark-gray"
								style={{ verticalAlign: "middle", marginRight: 2 }}
							/>
							Tax Forms
						</ActionList>
					</div>
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	// console.log('state',state && state.timesheets)
	const { name, peNumber, currency } = { ...state.company?.settings };
	const { payments, detailsAtDept, credentials, year } = { ...state.submissionForm };

	const monthlyPayments = payments?.reduce((prev, curr) => {
		prev[curr.month] = {
			...prev[curr.month],
			...curr,
		};
		return prev;
	}, {});
	const paymentsPerMonth = Object.entries({ ...monthlyPayments }).map(([field, value]) => ({
		...value,
		month: field,
	}));
	const totalPaid = paymentsPerMonth?.reduce((prev, curr) => {
		const sscToAdd = parseFloat(curr.ssc) || 0;
		const mfcToAdd = parseFloat(curr.mfc) || 0;
		const fssToAdd = parseFloat(curr.fss) || 0;

		return prev + sscToAdd + mfcToAdd + fssToAdd;
	}, 0);
	const existingFS5s = state.fssReports?.filter((item) => item.type == "fs5");
	const totals = {
		paid: {
			SSC: sumBy(paymentsPerMonth, ({ ssc }) => parseFloat(ssc)),
			MFC: sumBy(paymentsPerMonth, ({ mfc }) => parseFloat(mfc)),
			FSS: sumBy(paymentsPerMonth, ({ fss }) => parseFloat(fss)),
			total: sumBy(paymentsPerMonth, ({ fss, mfc, ssc }) => parseFloat(fss) + parseFloat(mfc) + parseFloat(ssc)),
		},
		fs5: {
			SSC: sumBy(existingFS5s, ({ SSC }) => parseFloat(SSC)),
			MFC: sumBy(existingFS5s, ({ MFC }) => parseFloat(MFC)),
			FSS: sumBy(
				existingFS5s,
				({ otFSS, ptFSS, ftFSS }) => parseFloat(otFSS) + parseFloat(ptFSS) + parseFloat(ftFSS),
			),
			total: sumBy(
				existingFS5s,
				({ MFC, SSC, otFSS, ptFSS, ftFSS }) =>
					parseFloat(SSC) + parseFloat(MFC) + parseFloat(otFSS) + parseFloat(ptFSS) + parseFloat(ftFSS),
			),
		},
	};
	return {
		companyName: name,
		paymentsPerMonth,
		payments,
		peNumber,
		currency,
		existingFS5s:
			existingFS5s?.length > 0
				? existingFS5s?.reduce((prev, curr) => {
						const month = format(parse(curr.month + "-01", "yyyy-MM-dd", new Date()), "MMMM");
						prev[month] = curr;
						return prev;
				  }, {})
				: undefined,
		totals,
		submissionForm: state.submissionForm,
		loggedIn: state.submissionForm?.credentials,
		totalPaid,
		credentials,
		validated: state.submissionForm?.validationResult?.validated,
	};
};

const mapDispatchToProps = (dispatch) => ({
	openEIDModal: () => dispatch(FSSREPORTACTIONS.openEIDModal(true)),
	openFS7ValidationModal: (year, credentials) => dispatch(FSSREPORTACTIONS.validateFS7({ year, credentials })),
	submitFS7: (year, credentials) => dispatch(FSSREPORTACTIONS.validateFS7({ year, credentials, submit: true })),
	downloadFSSReport: (data) => dispatch(FSSREPORTACTIONS.downloadFSSReport(data)),
	listFssReports: (data) => dispatch(FSSREPORTACTIONS.listFssReports(data)),
	openRegenerateFormsModal: ({ status, year, reportType }) =>
		dispatch(FSSREPORTACTIONS.openRegenerateFormsModal({ status, year, reportType })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Submissions);
